import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderKanban, ArrowRight, Phone, CreditCard } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { COMPANY } from "@/lib/constants";

const STATUS_COLORS = {
  planning: "bg-blue-100 text-blue-700", scheduled: "bg-purple-100 text-purple-700",
  in_progress: "bg-primary/10 text-primary", on_hold: "bg-amber-100 text-amber-700", completed: "bg-green-100 text-green-700",
};
const label = (s) => s.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function PortalDashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}).finally(() => setLoaded(true)); }, []);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl font-extrabold">Welcome, {user?.name?.split(" ")[0]}</h1>
        <p className="text-muted-foreground">Track your concrete projects with CG Concrete.</p>
      </div>

      {loaded && projects.length === 0 ? (
        <div className="bg-card border border-border p-10 text-center">
          <FolderKanban className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
          <h2 className="font-heading text-xl font-bold">No projects yet</h2>
          <p className="text-muted-foreground mt-1 max-w-md mx-auto">Once we start work on your job, it'll show up here with status updates, photos, and documents.</p>
          <Button asChild className="mt-5 bg-primary rounded-sm font-bold"><a href={`tel:${COMPANY.phoneRaw}`}><Phone className="h-4 w-4 mr-1" /> Call {COMPANY.phone}</a></Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {projects.map((p) => (
            <Link to={`/portal/projects/${p.id}`} key={p.id} className="bg-card border border-border p-6 hover:border-primary transition-colors group" data-testid={`portal-project-${p.id}`}>
              <div className="flex items-start justify-between">
                <div><h3 className="font-heading font-bold text-lg">{p.title}</h3><p className="text-sm text-muted-foreground">{p.service_type}</p></div>
                <span className={`text-xs font-bold px-2.5 py-1 ${STATUS_COLORS[p.status]}`}>{label(p.status)}</span>
              </div>
              <div className="mt-5">
                <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">Progress</span><span className="font-bold">{p.progress}%</span></div>
                <Progress value={p.progress} className="h-2" />
              </div>
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{p.updates?.length || 0} updates</span>
                <span className="font-bold text-primary flex items-center gap-1 group-hover:gap-2 transition-all">View <ArrowRight className="h-4 w-4" /></span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="bg-muted border border-border p-6 flex items-center gap-4">
        <CreditCard className="h-6 w-6 text-muted-foreground shrink-0" />
        <div>
          <p className="font-bold">Payments coming soon</p>
          <p className="text-sm text-muted-foreground">Online deposits and invoice payments will be available here in a future update.</p>
        </div>
      </div>
    </div>
  );
}
